import './index.css'

import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { ExpandAndCollapse } from '@flowwai-frontend/shared/common-components'
import { PaddedChevronDownIcon } from '@flowwai-frontend/design-system/icons'

import useHover from '../../hooks/useHover'
import NavItemModel from '../../stores/models/NavItemModel/NavItemModel'
import { UnionNavItemModel } from '../../types'

import DefaultChildNavItem from '../DefaultChildNavItem/DefaultChildNavItem'

import {
   chevronIconStyles,
   childrenContainerClassName,
   containerClassName,
   iconContainerClassName,
   nameAndIconContainerClassName,
   nameClassName
} from './styles'

interface Props {
   item: NavItemModel
   onActiveNavItem: (itemId: string) => void
   isExactMatch?: (id: string) => boolean
}

const DefaultExpandableNavItem = (props: Props): React.ReactElement => {
   const { item, onActiveNavItem } = props

   const { name, iconId } = item

   const { theme } = useAppTheme()
   const { defaultExpandableNavItem: navItemTheme } = theme.navbar

   const hoverState = useHover()

   const renderChildItem = (child: UnionNavItemModel): React.ReactElement => {
      if (child instanceof NavItemModel) {
         return (
            <DefaultChildNavItem
               key={child.id}
               item={child}
               indentationLevel={1}
               onActiveNavItem={onActiveNavItem}
               isExactMatch={props.isExactMatch}
            />
         )
      }

      throw new Error('Child item must be a NavItemModel')
   }

   const renderChildren = (): React.ReactElement => (
      <div className={childrenContainerClassName}>
         {item.children.map(child => renderChildItem(child))}
      </div>
   )
   const renderIcon = (): React.ReactElement | null => {
      if (!iconId) return null

      return (
         <div className={iconContainerClassName}>
            <svg width={20} height={20} fill='none'>
               <use xlinkHref={`/assets/icons/iconsSprite.svg#${iconId}`} />
            </svg>
         </div>
      )
   }

   const onToggle = (): void => {
      item.setIsOpen(!item.isOpen)
   }

   const renderTrigger = (): React.ReactElement | null => (
      <div
         onClick={onToggle}
         className={cn(containerClassName, navItemTheme.textColor, {
            [navItemTheme.hoverBgColor]: hoverState.isHovered
         })}
         role='button'
         onMouseEnter={hoverState.handleMouseEnter}
         onMouseLeave={hoverState.handleMouseLeave}
      >
         <div className={nameAndIconContainerClassName}>
            {renderIcon()}
            <p className={nameClassName} title={name}>
               {name}
            </p>
         </div>
         <div className={chevronIconStyles(item.isOpen)}>
            <PaddedChevronDownIcon
               height={20}
               width={20}
               stroke={navItemTheme.chevronIconColor}
            />
         </div>
      </div>
   )

   return (
      <>
         {renderTrigger()}
         <ExpandAndCollapse
            isOpened={item.isOpen}
            shouldUnmountCollapsedComponent={false}
            containerClassName={'default-expandable-container'}
         >
            {renderChildren()}
         </ExpandAndCollapse>
      </>
   )
}

export default observer(DefaultExpandableNavItem)
